import { PluginObject } from 'vue'
import { PostMessage } from './types'
import { setCordova, setCapacitor } from '@/utils/window'
import { Cordova, Window, CapacitorInfo } from '@/types/window'
import { isWorker, isRelative } from '@/types/user'
import PushHandler from '@/pushHandler'
import { PostMessageOrigin } from '@/utils'

const token = {
  get() {
    const token = localStorage.getItem('token')
    const refreshToken = localStorage.getItem('refresh')
    return { token, refreshToken }
  },
  set(data: { token: string; refreshToken: string }) {
    localStorage.setItem('token', data.token)
    localStorage.setItem('refresh', data.refreshToken)
  }
}

const sendMessageTypes = {
  getSession: 'getSession',
  saveSession: 'saveSession',
  vueDidLogin: 'vueDidLogin',
  vueDidLoad: 'vueDidLoad',
  openSafariView: 'openSafariView',
  openBrowser: 'openBrowser',
  saveStorage: 'saveStorage',
  getStorage: 'getStorage',
  setViewport: 'setViewport',
  reloadApp: 'reloadApp'
}
export type SendMessageType = keyof typeof sendMessageTypes

const receiveMessageTypes = {
  error: 'error',
  setSession: 'setSession',
  setStorage: 'setStorage',
  activateApp: 'activateApp',
  setCordovaObject: 'setCordovaObject',
  setCapacitorObject: 'setCapacitorObject',
  setFCMToken: 'setFCMToken',
  receivedPushMessage: 'receivedPushMessage',
  deniedPushPermission: 'deniedPushPermission'
}
export type ReceiveMessageType = keyof typeof receiveMessageTypes

export const Messenger: PostMessage = {
  _origin: '*',
  _prefix: 'hl3message::',
  _separator: '::',
  _callbacks: {},
  _listener: {},
  initialize(origin: string) {
    this._origin = origin || this._origin
    window.addEventListener('message', this._receiveMessage.bind(this))
  },
  emitVueDidLoad() {
    this._sendMessageToParent(
      sendMessageTypes.vueDidLoad as SendMessageType,
      []
    )
  },
  emitVueDidLogin() {
    this._sendMessageToParent(
      sendMessageTypes.vueDidLogin as SendMessageType,
      []
    )
  },
  getSession() {
    this._sendMessageToParent(
      sendMessageTypes.getSession as SendMessageType,
      []
    )
  },
  saveSession(token, refresh) {
    const tokens = {
      token,
      refreshToken: refresh
    }
    this._sendMessageToParent(sendMessageTypes.saveSession as SendMessageType, [
      tokens
    ])
  },
  getStorage(key) {
    this._sendMessageToParent(sendMessageTypes.getStorage as SendMessageType, [
      key
    ])
  },
  saveStorage(data) {
    this._sendMessageToParent(sendMessageTypes.saveStorage as SendMessageType, [
      data
    ])
  },
  addEventListener(type, listener) {
    const listeners = this._listener[type] || []
    this._listener[type] = [...listeners, listener]
  },
  showCallReceiveView(callInfo) {
    console.log('%c postMessage showCallReceiveView %c', 'color:blue;', '')
    console.log(callInfo)
    this._sendMessageToParent(
      sendMessageTypes.openSafariView as SendMessageType,
      [this._makeCallReceiveUrl(callInfo)]
    )
  },
  showCallDispatchView(callInfo) {
    console.log('%c postMessage showCallDispatchView %c', 'color:blue;', '')
    console.log(callInfo)
    this._sendMessageToParent(
      sendMessageTypes.openSafariView as SendMessageType,
      [this._makeCallDispatchUrl(callInfo)]
    )
  },
  showCameraView() {
    const url = this._makeBaseUrl('image/create/inapp')
    console.log('%c postMessage showCameraView %c', 'color:blue;', '')
    console.log(url)
    this._sendMessageToParent(
      sendMessageTypes.openSafariView as SendMessageType,
      [url]
    )
  },
  showPdCameraView(patientId, isPatient) {
    const baseUrl = isPatient ? 'pd_create_inapp' : 'w/pd/create/inapp'
    const url = this._makeBaseUrl(`${baseUrl}/${patientId}`)
    console.log('%c postMessage showPdCameraView %c', 'color:blue;', '')
    console.log(url)
    this._sendMessageToParent(
      sendMessageTypes.openSafariView as SendMessageType,
      [url]
    )
  },
  showPatientQR() {
    const url = this._makeBaseUrl('qr')
    console.log('%c postMessage showPatientQR %c', 'color:blue;', '')
    console.log(url)
    this._sendMessageToParent(
      sendMessageTypes.openSafariView as SendMessageType,
      [url]
    )
  },
  showDispensationQR(patientId, regular) {
    const url = `${this._makeBaseUrl(
      'w/dispensation/qr'
    )}/${patientId}/${regular}`
    console.log('%c postMessage showDispensationQR %c', 'color:blue;', '')
    console.log(url)
    this._sendMessageToParent(
      sendMessageTypes.openSafariView as SendMessageType,
      [url]
    )
  },
  showPrescriptionQR(patientId) {
    const url = `${this._makeBaseUrl('w/prescription/qr')}/${patientId}`
    console.log('%c postMessage showDispensationQR %c', 'color:blue;', '')
    console.log(url)
    this._sendMessageToParent(
      sendMessageTypes.openSafariView as SendMessageType,
      [url]
    )
  },
  openFileDownload({
    isPatient,
    isRelative,
    type,
    patientId,
    fileId,
    fileName
  }) {
    const prefix = isRelative ? 'r/' : isPatient ? '' : 'w/'
    const targetPath = `${prefix}filedownload`
    const baseUrl = this._makeBaseUrl(targetPath)
    const url = `${baseUrl}/${type}/${patientId}/${fileId}/${fileName}`
    this._sendMessageToParent(sendMessageTypes.openBrowser as SendMessageType, [
      url
    ])
  },
  setViewport(setting) {
    this._sendMessageToParent(sendMessageTypes.setViewport as SendMessageType, [
      setting
    ])
  },
  openExternalUrl(url) {
    this._sendMessageToParent(sendMessageTypes.openBrowser as SendMessageType, [
      url
    ])
  },
  reloadApp() {
    this._sendMessageToParent(sendMessageTypes.reloadApp as SendMessageType, [])
  },
  _sendMessageToParent(type, args) {
    this._sendMessage(type + this._separator + JSON.stringify(args))
  },
  _sendMessage(data) {
    if (window == window.parent) {
      this._runListener(receiveMessageTypes.error as ReceiveMessageType, [
        new Error('no parent window')
      ])
      return
    }
    window.parent.postMessage(this._prefix + data, this._origin)
  },
  _receiveMessage(event) {
    if (this._origin != '*' && event.origin !== this._origin)
      throw new Error('CORS Error')
    try {
      if (!this._checkMessage(event.data)) return
      const message = event.data.replace(this._prefix, '')
      const type = this._getDataFromMessage(message).type as ReceiveMessageType
      const data = JSON.parse(this._getDataFromMessage(message).data)
      switch (type) {
        case receiveMessageTypes.activateApp:
          this._runListener(type, [])
          break
        case receiveMessageTypes.setCordovaObject:
          this._setCordova(data)
          break
        case receiveMessageTypes.setCapacitorObject:
          this._setCapacitor(data)
          break
        case receiveMessageTypes.setSession:
          this._setSession(data)
          break
        default:
          this._runListener(type, data)
          break
      }
    } catch (e) {
      alert(e)
    }
  },
  _runListener(type, args) {
    const listeners = this._listener[type]
    if (!listeners || !listeners.length) return
    listeners.forEach(listener => {
      listener(...args)
    })
  },
  _setSession(args) {
    const data = args[0]
    if (!data.token || typeof data.token != 'string') {
      this._runListener(receiveMessageTypes.error as ReceiveMessageType, [])
    } else {
      token.set(data)
      this._runListener(
        receiveMessageTypes.setSession as ReceiveMessageType,
        args
      )
    }
  },
  _setCordova(args) {
    const cordovaObj = args[0] as Cordova
    setCordova(cordovaObj)
  },
  _setCapacitor(args) {
    const capacitorObj = args[0] as CapacitorInfo
    setCapacitor(capacitorObj)
  },
  _getDataFromMessage(message) {
    const datas = message.split(this._separator)
    return {
      type: datas[0],
      data: datas[1]
    }
  },
  _checkMessage(message) {
    const reg = new RegExp('^' + this._prefix + '.+' + this._separator + '.?')
    return reg.test(message)
  },
  _makeBaseUrl(path) {
    const origin = new URL(location.href).origin
    const w = window as Window
    const platformId = w.cordova?.platformId
    const targetPath = path
    const tokens = token.get()
    const tokenInfo = `${tokens.token}/${tokens.refreshToken}`
    return `${origin}/${targetPath}/${tokenInfo}/${platformId}`
  },
  _makeCallReceiveUrl(data) {
    const prefix = data.isRelative ? 'r/' : data.isPatient ? '' : 'w/'
    const targetPath = `${prefix}callreceive`
    const baseUrl = this._makeBaseUrl(targetPath)
    const callInfo = `${data.roomId}/${data.pin}/${data.secret}/${String(
      data.videoEnable
    )}`
    const fromName =
      encodeURIComponent(data.fromUser.firstName) +
      '/' +
      encodeURIComponent(data.fromUser.lastName)
    const fromUser = `${data.fromUser.id}/${data.fromUser.isPatient}/${fromName}`
    const ocpName =
      encodeURIComponent(data.fromOccupation.name) +
      '/' +
      encodeURIComponent(data.fromOccupation.shortName)
    const fromOccupation = `${data.fromOccupation.code}/${ocpName}`
    return `${baseUrl}/${callInfo}/${fromUser}/${fromOccupation}`
  },
  _makeCallDispatchUrl(data) {
    const prefix = data.isRelative ? 'r/' : data.isPatient ? '' : 'w/'
    const targetPath = `${prefix}calldispatch`
    const baseUrl = this._makeBaseUrl(targetPath)
    const callInfo = `${String(data.videoApiSecret)}`
    const toUsers = `${encodeURIComponent(
      JSON.stringify(
        data.toUsers.map(user => ({
          id: user.id,
          isPatient: !isWorker(user),
          isRelative: isRelative(user)
        }))
      )
    )}`
    return `${baseUrl}/${callInfo}/${toUsers}`
  }
}

Messenger.initialize(PostMessageOrigin)
Messenger.addEventListener(
  'setFCMToken',
  PushHandler.handleFCMToken.bind(PushHandler)
)
Messenger.addEventListener(
  'receivedPushMessage',
  PushHandler.handleMessage.bind(PushHandler)
)
Messenger.addEventListener(
  'deniedPushPermission',
  PushHandler.handlePermissionDenied.bind(PushHandler)
)
Messenger.emitVueDidLoad()

const Plugin: PluginObject<undefined> = {
  install: Vue => {
    const postMessage = Messenger

    Vue.$postMessage = postMessage
    Object.defineProperty(Vue.prototype, '$postMessage', {
      get() {
        return postMessage
      }
    })
  }
}

export default Plugin
